import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import { Loading } from '@framework/ui/atoms/Loading';
import type { GroupMemberInvitation } from '@group/domain';
import type { GroupMemberSignUpMethod } from '@group/domain/GroupEntity';
import { useFirebaseAuthRedirectWithGoogle } from '@user/Login/useFirebaseAuthRedirectWithGoogle';
import { useFirebaseAuthRedirectWithMicrosoft } from '@user/Login/useFirebaseAuthRedirectWithMicrosoft';
import { toast } from 'react-hot-toast';
import { UserSignUpWithEmailModal } from '../UserSignUpWithEmailModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle, faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { UserSignUpRequestWithEmailModal } from '@user/pages/HomePage/UserSignUpRequestWithEmailModal';

type Props = {
  availableSignUpMethods: GroupMemberSignUpMethod;
  invitation?: GroupMemberInvitation;
  token?: string | null;
  onLogin(): void;
};

export const SignUpMethodMenu: React.FC<Props> = ({ availableSignUpMethods, invitation, token, onLogin }: Props) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const firebaseAuthRedirectWithGoogle = useFirebaseAuthRedirectWithGoogle();
  const firebaseAuthRedirectWithMicrosoft = useFirebaseAuthRedirectWithMicrosoft();

  const handleLoginWithGoogle = useCallback(async () => {
    setProcessing(true);
    try {
      // 成功時はページが redirect で離脱するため processing は戻さない。
      await firebaseAuthRedirectWithGoogle({ withInvitation: !!invitation });
    } catch (e) {
      console.error(e);
      toast.error(t('auth.login.processingError'));
      setProcessing(false);
    }
  }, [firebaseAuthRedirectWithGoogle, invitation, t]);

  const handleLoginWithMicrosoft = useCallback(async () => {
    setProcessing(true);
    try {
      // 成功時はページが redirect で離脱するため processing は戻さない。
      await firebaseAuthRedirectWithMicrosoft({ withInvitation: !!invitation });
    } catch (e) {
      console.error(e);
      toast.error(t('auth.login.processingError'));
      setProcessing(false);
    }
  }, [firebaseAuthRedirectWithMicrosoft, invitation, t]);

  const [showModal, setShowModal] = useState(false);
  const handleShowModal = useCallback(() => setShowModal(true), []);
  const handleCloseModal = useCallback(() => setShowModal(false), []);

  useEffect(() => {
    // このグループへの参加時に利用可能なログイン方法が1つだけ存在する場合には、そのログイン方法名を得る。
    const method = availableSignUpMethods.getSingleAvailableMethod();

    if (method === 'email') {
      handleShowModal();
      return;
    }
  }, [availableSignUpMethods, handleShowModal]);

  if (processing) {
    return (
      <div className="flex w-80 flex-col space-y-4 px-5 py-2">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex w-100 flex-col space-y-4 px-5 py-2">
      {availableSignUpMethods.availableOf('google') && (
        <FlatButton color="brand" onClick={handleLoginWithGoogle}>
          <span className="pr-2 text-lg">
            <FontAwesomeIcon icon={faGoogle} />
          </span>
          {t('auth.inviteMenu.signUp.withGoogle')}
        </FlatButton>
      )}

      {availableSignUpMethods.availableOf('microsoft') && (
        <FlatButton color="brand" onClick={handleLoginWithMicrosoft}>
          <span className="pr-2 text-lg">
            <FontAwesomeIcon icon={faMicrosoft} />
          </span>
          {t('auth.inviteMenu.signUp.withMicrosoft')}
        </FlatButton>
      )}

      {availableSignUpMethods.availableOf('email') && (
        <FlatButton color="brand" onClick={handleShowModal}>
          {t('auth.inviteMenu.signUp.withEmail')}
        </FlatButton>
      )}
      <div className="pt-4 text-center">
        {t('auth.inviteMenu.signUp.hasAccount')}
        <div className="py-2">
          <FlatButton onClick={onLogin}>{t('common.action.login')}</FlatButton>
        </div>
      </div>

      {invitation && (
        <UserSignUpWithEmailModal
          isOpen={showModal}
          onClose={handleCloseModal}
          mailAddress={invitation.mailAddress}
          groupId={invitation.senderGroup.groupId}
          invitationId={invitation.id}
          token={token ?? null}
        />
      )}
      {!invitation && <UserSignUpRequestWithEmailModal isOpen={showModal} onClose={handleCloseModal} />}
    </div>
  );
};
